import {
  createContext,
  useState,
  ReactNode,
  useEffect,
  useRef,
  useContext,
  useCallback,
} from 'react';
import { useLocation } from 'react-router-dom';

import { useReportState } from '../hooks/useReportState';

import { ChatContext } from './ChatContext';

import type { ReportMonitor } from '@ltvco/refresh-lib/v1';

export interface MonitoringState {
  selectedMonitor: ReportMonitor | null;
  openMonitoring: (monitor: ReportMonitor) => void;
  closeMonitoring: () => void;
}

interface ChatLayoutContextType {
  showLanding: boolean;
  setShowLanding: (show: boolean) => void;
  hasCompletedAnimation: boolean;
  setHasCompletedAnimation: (hasCompleted: boolean) => void;
  isLogoAnimating: boolean;
  setIsLogoAnimating: (isAnimating: boolean) => void;
  logoAnimationPhase: 'center' | 'moving-up' | 'moving-left' | 'header';
  setLogoAnimationPhase: (
    phase: 'center' | 'moving-up' | 'moving-left' | 'header'
  ) => void;
  closeAllPanels: () => void;
  setCloseAllPanels: (fn: () => void) => void;
  reportState: ReturnType<typeof useReportState>;
  monitoringState: MonitoringState;
}

export const ChatLayoutContext = createContext<ChatLayoutContextType>({
  showLanding: true,
  setShowLanding: () => {},
  hasCompletedAnimation: false,
  setHasCompletedAnimation: () => {},
  isLogoAnimating: false,
  setIsLogoAnimating: () => {},
  logoAnimationPhase: 'center',
  setLogoAnimationPhase: () => {},
  closeAllPanels: () => {},
  setCloseAllPanels: () => {},
  reportState: {} as ReturnType<typeof useReportState>,
  monitoringState: {
    selectedMonitor: null,
    openMonitoring: () => {},
    closeMonitoring: () => {},
  },
});

export const ChatLayoutProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const { messages } = useContext(ChatContext);
  const prevPathRef = useRef<string>(location.pathname);
  const closeAllPanelsRef = useRef<(() => void) | null>(null);

  const [showLanding, setShowLanding] = useState(true);
  const [hasCompletedAnimation, setHasCompletedAnimation] = useState(false);
  const [isLogoAnimating, setIsLogoAnimating] = useState(false);
  const [logoAnimationPhase, setLogoAnimationPhase] = useState<
    'center' | 'moving-up' | 'moving-left' | 'header'
  >('center');

  const reportState = useReportState();
  const { closeAllPanels: closeReportStatePanels } = reportState;

  const [selectedMonitor, setSelectedMonitor] = useState<ReportMonitor | null>(
    null
  );

  const closeMonitoring = useCallback(() => {
    setSelectedMonitor(null);
  }, []);

  const openMonitoring = useCallback(
    (monitor: ReportMonitor) => {
      closeReportStatePanels();
      setSelectedMonitor(monitor);
    },
    [closeReportStatePanels]
  );

  const monitoringState: MonitoringState = {
    selectedMonitor,
    openMonitoring,
    closeMonitoring,
  };

  const closeAllPanels = useCallback(() => {
    if (closeAllPanelsRef.current) {
      closeAllPanelsRef.current();
    }
    closeReportStatePanels();
    setSelectedMonitor(null);
  }, [closeReportStatePanels]);

  const setCloseAllPanels = useCallback((fn: () => void) => {
    closeAllPanelsRef.current = fn;
  }, []);

  const isMonitoredRoute = location.pathname.includes('chat/library/monitored');

  useEffect(() => {
    if (!isMonitoredRoute && selectedMonitor) {
      setSelectedMonitor(null);
    }
  }, [isMonitoredRoute, selectedMonitor]);

  const isConversationRoute = location.pathname.startsWith(
    '/chatbv/chat/conversation'
  );
  const isBaseChatRoute = location.pathname === '/chatbv/chat';

  useEffect(() => {
    const prevPath = prevPathRef.current;
    prevPathRef.current = location.pathname;

    if (isBaseChatRoute && prevPath === '/chatbv/chat/conversation') {
      setShowLanding(true);
      setLogoAnimationPhase('center');
      setHasCompletedAnimation(false);
      setIsLogoAnimating(false);
      closeAllPanels();
    }
    // If on conversation route with messages, ensure we're not showing landing
    else if (isConversationRoute && showLanding && messages.length > 0) {
      setShowLanding(false);
      setHasCompletedAnimation(true);
    }
    // If on base route, ensure landing is shown
    else if (isBaseChatRoute && !showLanding && messages.length === 0) {
      setShowLanding(true);
      setLogoAnimationPhase('center');
      setHasCompletedAnimation(false);
      closeAllPanels();
    }
  }, [
    location.pathname,
    isConversationRoute,
    isBaseChatRoute,
    messages.length,
    showLanding,
    closeAllPanels,
  ]);

  return (
    <ChatLayoutContext.Provider
      value={{
        showLanding,
        setShowLanding,
        hasCompletedAnimation,
        setHasCompletedAnimation,
        isLogoAnimating,
        setIsLogoAnimating,
        logoAnimationPhase,
        setLogoAnimationPhase,
        closeAllPanels,
        setCloseAllPanels,
        reportState,
        monitoringState,
      }}
    >
      {children}
    </ChatLayoutContext.Provider>
  );
};
